import React, { FC, useCallback, Fragment } from "react"
import { Checkbox } from "dash-styled"

import { APIAppPlan } from "../../../../../../../../../../../../Models/API/APIAppPlans"
import { MetricRequest } from "../../../../../../../../../../../../Models/Client/MetricRequest"
import { Confirm } from "../../../../../../../../../../../Common/Modal/Confirm"
import { useBoolean } from "../../../../../../../../../../../../Hooks/useBoolean"
import {
    FetchState,
    getValue
} from "../../../../../../../../../../../../Models/Client/FetchState"
import {
    FormFieldsPlan,
    defaultWorkflowConfig,
    setAllPlansVisibility,
    togglePlanVisibility
} from "../FormFields"
import { PlanPanel } from "./PlanPanel"
import { PlanPanelAdminFooter } from "./PlanPanelAdminFooter"

interface AppSettingsPlanProps {
    isSysAdmin: boolean
    plans: APIAppPlan[]
    metricsFetch: FetchState<MetricRequest[]>
    qualityName: string | undefined
    value: FormFieldsPlan
    onChange: (value: { plan: FormFieldsPlan }) => void
}

export const AppSettingsPlan: FC<AppSettingsPlanProps> = ({
    isSysAdmin,
    plans,
    metricsFetch,
    qualityName,
    value,
    onChange
}) => {
    const [isDialogShown, showDialog, hideDialog] = useBoolean(false)
    const hideAll = value.workflowConfigs.every(
        config => config.hiddenFromCustomer
    )
    const handleChange = useCallback(
        (value: FormFieldsPlan) => {
            onChange({
                plan: value
            })
        },
        [onChange]
    )

    const handleToggleHideAll = useCallback(
        () => handleChange(setAllPlansVisibility(value, hideAll)),
        [value, handleChange, hideAll]
    )
    const handleHideAllPlans = useCallback(() => {
        handleChange(setAllPlansVisibility(value, false))
        hideDialog()
    }, [handleChange, value, hideDialog])
    const renderPlan = useCallback(
        (plan: APIAppPlan, index: number, arr: APIAppPlan[]) => {
            const isSelecting = value.selectedWorkflow === plan.workflow
            const explicitConfig = value.workflowConfigs.get(plan.workflow)
            const config = explicitConfig ?? defaultWorkflowConfig

            if (!isSysAdmin && config.hiddenFromCustomer) {
                return <Fragment key={plan.workflow} />
            }

            const handleSelect = () => {
                if (
                    !hideAll &&
                    (value.workflowConfigs.get(plan.workflow)
                        ?.hiddenFromCustomer ??
                        false)
                ) {
                    handleChange({
                        workflowConfigs: value.workflowConfigs.update(
                            plan.workflow,
                            config => ({
                                ...config,
                                hiddenFromCustomer: false
                            })
                        ),
                        selectedWorkflow: plan.workflow
                    })
                    return
                }
                handleChange({ ...value, selectedWorkflow: plan.workflow })
            }
            const handleToggleVisibility = (isVisible: boolean) => {
                if (
                    hideAll &&
                    isVisible &&
                    value.selectedWorkflow !== plan.workflow
                ) {
                    handleChange(setAllPlansVisibility(value, true))
                    return
                }

                if (
                    !isVisible &&
                    value.selectedWorkflow === plan.workflow &&
                    value.workflowConfigs.some(
                        (c, wf) => wf !== plan.workflow && !c.hiddenFromCustomer
                    )
                ) {
                    showDialog()
                    return
                }

                handleChange(togglePlanVisibility(value, plan.workflow))
            }

            const adminFooter = isSysAdmin ? (
                <PlanPanelAdminFooter
                    isVisibleToCustomer={!config.hiddenFromCustomer}
                    onChangeVisibleToCustomer={handleToggleVisibility}
                />
            ) : null

            return (
                <PlanPanel
                    key={plan.workflow}
                    className={index === arr.length - 1 ? "" : "mb-3"}
                    qualityName={qualityName}
                    model={plan}
                    metric={getValue(metricsFetch)?.find(
                        metric => metric.workflow === plan.workflow
                    )}
                    priceOverride={config.priceOverride}
                    isSelected={isSelecting}
                    footer={adminFooter}
                    onSelect={handleSelect}
                />
            )
        },
        [
            value,
            isSysAdmin,
            qualityName,
            metricsFetch,
            hideAll,
            handleChange,
            showDialog
        ]
    )

    return (
        <>
            <ul>{plans.map(renderPlan)}</ul>
            {isSysAdmin ? (
                <Checkbox
                    className="mt-5 mb-0"
                    label="Hide plans from customer"
                    variant="modern"
                    isChecked={hideAll}
                    onChange={handleToggleHideAll}
                />
            ) : null}
            {isDialogShown ? (
                <Confirm
                    title="Hide all plans?"
                    description="The selected plan will always be visible to the customer, unless all plans are invisible."
                    onClickYes={handleHideAllPlans}
                    onClickNo={hideDialog}
                    onClose={hideDialog}
                    yesLabel="Hide all plans"
                    noLabel="Keep visible"
                />
            ) : null}
        </>
    )
}
